import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, MapPin, Phone, Mail, Instagram, Facebook, Menu, X, ShoppingBag, Navigation, Award, Baby, Glasses, Gem, ScanFace, ShieldCheck, ChevronLeft, ChevronRight, Tag } from "lucide-react";
import { Reveal } from "../Reveal";
import { useLang } from "@/i18n";
import LangMenu from "../LangMenu";
import Prenota from "../Prenota";
import CartDrawer from "../CartDrawer";
import TryOnModal from "../TryOnModal";
import Services from "../Services";
import Reviews from "../Reviews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Risolve un'immagine: URL assoluto o percorso statico ("/images/..") usati così come sono;
// altrimenti è una chiave dell'object storage servita da /api/files
const resolveImg = (p) => (!p ? "" : /^(https?:|\/)/.test(p) ? p : `${API}/files/${p}`);
const eur = (n) => n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const VISTA = "Collezione Vista";
const scrollToPrenota = () => document.querySelector("#prenota")?.scrollIntoView({ behavior: "smooth" });

const V2Header = ({ cartCount = 0, onCart }) => {
  const { t } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [
    ["#v2-storia", t("chi_overline")],
    ["#servizi", t("nav_servizi")],
    ["#v2-prodotti", t("v2_nav_prodotti")],
    ["#recensioni", t("nav_recensioni")],
    ["/faq", t("nav_faq")],
    ["#v2-contatti", t("v2_nav_contatti")],
    ["#prenota", t("cta_prenota")],
  ];
  return (
    <header data-testid="v2-header" className="absolute top-0 left-0 right-0 z-30 text-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <a href="#v2-top" data-testid="v2-logo" className="flex flex-col items-start leading-none" aria-label="Stylottica Ceglie">
          <img
            src="/images/logo-stylottica-mark.png?v=3"
            alt="Stylottica Ceglie"
            className="brand-logo h-11 md:h-12 w-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
          />
          <span className="mt-1.5 text-white font-modern text-xs md:text-sm font-semibold tracking-tight drop-shadow-[0_1px_5px_rgba(0,0,0,0.9)]">
            Stylottica Ceglie
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {links.map(([href, label]) =>
            href.startsWith("/") ? (
              <Link key={href} to={href} className="text-[11px] uppercase tracking-[0.25em] text-white/80 hover:text-white transition-colors">
                {label}
              </Link>
            ) : (
              <a key={href} href={href} className="text-[11px] uppercase tracking-[0.25em] text-white/80 hover:text-white transition-colors">
                {label}
              </a>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <button
            onClick={onCart}
            data-testid="v2-cart-btn"
            aria-label="Carrello"
            className="relative rounded-full border border-white/40 p-2 hover:bg-white hover:text-[#1A1B18] transition-colors"
          >
            <ShoppingBag size={15} />
            {cartCount > 0 && (
              <span
                data-testid="v2-cart-count"
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#A86946] text-white text-[9px] font-bold flex items-center justify-center"
              >
                {cartCount}
              </span>
            )}
          </button>
          <LangMenu />
          <button
            className="md:hidden p-1.5"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="v2-mobile-menu-toggle"
            aria-label="Menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <nav data-testid="v2-mobile-menu" className="md:hidden bg-[#1A1B18]/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-5">
          {links.map(([href, label]) => (
            <a
              key={href}
              href={href}
              onClick={() => setMenuOpen(false)}
              data-testid={`v2-mobile-${href.slice(1)}`}
              className="text-xs uppercase tracking-[0.25em] text-white/80 hover:text-white transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>
      )}
    </header>
  );
};

const HERO_SLIDES = [
  { src: "/images/hero-slide-1.webp?v=hd2", alt: "Donna con occhiali da sole Twinset cat-eye neri al porto — Stylottica Ceglie" },
  { src: "/images/hero-slide-2.webp?v=hd", alt: "Uomo con occhiali da sole Police navigator dorati al tramonto — Stylottica Ceglie" },
  { src: "/images/hero-slide-3.webp?v=hd", alt: "Donna con occhiali da sole Twinset tartaruga tra le palme — Stylottica Ceglie" },
  { src: "/images/hero-slide-4.webp?v=hd", alt: "Uomo con occhiali da sole Police navigator in barca sul mare — Stylottica Ceglie" },
  { src: "/images/hero-slide-5.webp?v=hd", alt: "Donna con occhiali da sole Eyepetizer oro con lenti verdi al sole — Stylottica Ceglie" },
  { src: "/images/hero-slide-6.webp?v=hd", alt: "Donna elegante con occhiali da sole Liò geometrici verdi sulla costa mediterranea — Stylottica Ceglie" },
];

const V2Hero = ({ slides = HERO_SLIDES }) => {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  const restartAutoplay = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), 3500);
  };

  useEffect(() => {
    restartAutoplay();
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  return (
    <section id="v2-top" data-testid="v2-hero" className="relative h-[92vh] min-h-[560px] overflow-hidden">
      <div
        data-testid="v2-hero-slider"
        className="absolute inset-0 flex transition-transform duration-[1100ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {slides.map((s, i) => (
          <div key={s.src} className="relative w-full h-full shrink-0 overflow-hidden">
            <img
              src={s.src}
              alt={s.alt}
              width={1376}
              height={768}
              loading="eager"
              fetchPriority={i === 0 ? "high" : "auto"}
              decoding="async"
              className={`relative w-full h-full object-cover transition-transform duration-[3600ms] ease-out ${
                i === idx ? "md:scale-[1.06]" : "scale-100"
              }`}
            />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1B18]/70 via-[#1A1B18]/20 to-[#1A1B18]/30" />
      {/* Tono più fresco/verde coerente con il logo lime */}
      <div className="absolute inset-0 bg-[#BACD0E]/15 mix-blend-soft-light pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#4f6114]/18 via-transparent to-transparent pointer-events-none" />
      <div className="absolute bottom-6 right-6 md:right-12 z-20 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              setIdx(i);
              restartAutoplay();
            }}
            data-testid={`v2-hero-dot-${i}`}
            aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${i === idx ? "w-8 bg-white" : "w-3 bg-white/50 hover:bg-white/80"}`}
          />
        ))}
      </div>
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 hidden md:flex flex-col justify-end pb-16 md:pb-24 text-white">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.3em] text-white/80 mb-6">
            {t("v2_hero_overline")}
            <span className="hidden md:inline">{t("v2_hero_suffix")}</span>
          </p>
          <h1 className="font-modern text-5xl sm:text-6xl lg:text-8xl font-medium tracking-tighter leading-[1.02] max-w-4xl">
            {t("v2_hero_title")}
          </h1>
          <p className="mt-5 max-w-xl text-sm md:text-base text-white/85 leading-relaxed">{t("v2_hero_sub")}</p>
          <a
            href="#v2-prodotti"
            data-testid="v2-hero-cta"
            className="group mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white"
          >
            <span className="border-b border-white/60 pb-1 group-hover:border-[#A86946] group-hover:text-[#E8B48E] transition-colors">
              {t("v2_hero_cta")}
            </span>
            <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
        </Reveal>
      </div>
    </section>
  );
};

const HIGHLIGHTS = [
  { icon: Award, key: "hl_1" },
  { icon: Baby, key: "hl_2" },
  { icon: Glasses, key: "hl_3" },
  { icon: Gem, key: "hl_4" },
];

const CERTS = [
  { icon: Award, tk: "cert1t", sub: "Essilor", id: "essilor" },
  { icon: ShieldCheck, tk: "cert2t", sub: "Lenti Stellest", id: "stellest" },
];

const V2Storia = ({ photoUrl }) => {
  const { t } = useLang();
  const stats = [
    ["13+", t("v2_stat1")],
    ["2", t("v2_stat2")],
    ["5.0", t("v2_stat3")],
  ];
  return (
    <section id="v2-storia" data-testid="v2-storia" className="py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden group">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={t("chi_photo_alt")}
                  data-testid="chi-siamo-photo"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[420px] md:h-[560px] object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
              ) : (
                <div
                  data-testid="chi-siamo-photo-loading"
                  className="w-full h-[420px] md:h-[560px] bg-[#EDE9E1] animate-pulse"
                />
              )}
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#A86946] mb-4">{t("chi_overline")}</p>
              <h2 className="font-modern text-4xl md:text-6xl font-medium tracking-tighter leading-[1.05]">
                {t("v2_storia_title")}
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-8 space-y-6 text-base md:text-lg text-[#1A1B18]/70 leading-relaxed max-w-2xl">
                <p>{t("v2_storia_p1")}</p>
                <p>{t("v2_storia_p2")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 max-w-2xl">
                {HIGHLIGHTS.map((h, i) => (
                  <div key={h.key} data-testid={`chi-highlight-${i}`} className="flex items-center gap-3 border-t border-[#1A1B18]/10 pt-4">
                    <h.icon size={20} strokeWidth={1.5} className="text-[#A86946] shrink-0" />
                    <p className="text-sm font-medium tracking-tight">{t(h.key)}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <div data-testid="chi-certs" className="mt-8 max-w-2xl">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#1A1B18]/45 mb-3">{t("cert_label")}</p>

                <div className="flex flex-col sm:flex-row gap-4">
                  {CERTS.map((c) => (
                    <div
                      key={c.id}
                      data-testid={`chi-cert-${c.id}`}
                      className="flex items-center gap-4 bg-[#E8E4DA]/50 border border-[#DCD7CC] rounded-2xl px-5 py-4"
                    >
                      <c.icon size={26} strokeWidth={1.4} className="text-[#A86946] shrink-0" />
                      <div>
                        <p className="font-modern text-base font-medium leading-snug">{t(c.tk)}</p>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#1A1B18]/50 mt-0.5">{c.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
        <Reveal delay={0.15}>
          <blockquote className="my-12 md:my-16 font-modern text-2xl md:text-4xl font-medium tracking-tight text-center max-w-4xl mx-auto leading-snug">
            "{t("v2_quote")}"
          </blockquote>
        </Reveal>
        <Reveal>
          <div className="grid grid-cols-3 gap-px bg-[#1A1B18]/10 border-y border-[#1A1B18]/10">
            {stats.map(([v, l]) => (
              <div key={l} className="bg-[#FAF9F6] py-8 md:py-10 text-center">
                <p className="font-modern text-4xl md:text-5xl font-medium text-[#A86946]">{v}</p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#1A1B18]/50">{l}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const ProductGallery = ({ product: p, index: i }) => {
  const photos = [p.image_path, ...(p.images || [])];
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef({ dragging: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const pinchRef = useRef({ dist: 0, base: 1 });
  const hoverRef = useRef(null);

  const resetZoom = () => { setScale(1); setPan({ x: 0, y: 0 }); };
  const openZoom = () => { resetZoom(); setZoom(true); };
  const closeZoom = () => { setZoom(false); resetZoom(); };
  const clampScale = (s) => Math.min(4, Math.max(1, s));

  const onWheelZoom = (e) => {
    e.preventDefault();
    setScale((s) => {
      const ns = clampScale(s - e.deltaY * 0.0015 * s);
      if (ns === 1) setPan({ x: 0, y: 0 });
      return ns;
    });
  };
  const onDblZoom = (e) => { e.stopPropagation(); setScale((s) => (s > 1 ? 1 : 2.5)); setPan({ x: 0, y: 0 }); };
  const onPointerDown = (e) => { if (scale <= 1) return; panRef.current = { dragging: true, sx: e.clientX, sy: e.clientY, ox: pan.x, oy: pan.y }; };
  const onPointerMove = (e) => {
    if (!panRef.current.dragging) return;
    setPan({ x: panRef.current.ox + (e.clientX - panRef.current.sx), y: panRef.current.oy + (e.clientY - panRef.current.sy) });
  };
  const onPointerUp = () => { panRef.current.dragging = false; };
  const touchDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) { pinchRef.current = { dist: touchDist(e.touches), base: scale }; }
    else if (e.touches.length === 1 && scale > 1) { panRef.current = { dragging: true, sx: e.touches[0].clientX, sy: e.touches[0].clientY, ox: pan.x, oy: pan.y }; }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const ns = clampScale(pinchRef.current.base * (touchDist(e.touches) / (pinchRef.current.dist || 1)));
      setScale(ns);
      if (ns === 1) setPan({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && panRef.current.dragging) {
      setPan({ x: panRef.current.ox + (e.touches[0].clientX - panRef.current.sx), y: panRef.current.oy + (e.touches[0].clientY - panRef.current.sy) });
    }
  };
  const onTouchEnd = () => { panRef.current.dragging = false; };

  const startHover = () => {
    if (photos.length < 2 || hoverRef.current) return;
    hoverRef.current = setInterval(() => setIdx((x) => (x + 1) % photos.length), 1300);
  };
  const stopHover = () => {
    clearInterval(hoverRef.current);
    hoverRef.current = null;
    setIdx(0);
  };
  useEffect(() => () => clearInterval(hoverRef.current), []);

  useEffect(() => {
    if (!zoom) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") { closeZoom(); return; }
      if (photos.length < 2) return;
      if (e.key === "ArrowRight") { setIdx((x) => (x + 1) % photos.length); resetZoom(); }
      else if (e.key === "ArrowLeft") { setIdx((x) => (x - 1 + photos.length) % photos.length); resetZoom(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [zoom, photos.length]);

  const go = (dir) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((x) => (x + dir + photos.length) % photos.length);
    resetZoom();
    if (hoverRef.current) {
      clearInterval(hoverRef.current);
      hoverRef.current = setInterval(() => setIdx((x) => (x + 1) % photos.length), 1300);
    }
  };

  return (
    <div className="relative overflow-hidden bg-[#F0EDE5] group/gallery" onMouseEnter={startHover} onMouseLeave={stopHover}>
      <img
        src={`${API}/files/${photos[idx]}`}
        alt={p.nome}
        loading="lazy"
        decoding="async"
        onClick={openZoom}
        data-testid={`v2-gallery-img-${i}`}
        className="w-full h-80 object-cover cursor-zoom-in group-hover:scale-[1.03] transition-transform duration-700"
      />
      {photos.length > 1 && (
        <>
          <button
            onClick={go(-1)}
            data-testid={`v2-gallery-prev-${i}`}
            aria-label="Foto precedente"
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 backdrop-blur p-2 text-[#1A1B18] hover:bg-[#1A1B18] hover:text-white transition-colors duration-300 md:opacity-0 md:group-hover/gallery:opacity-100"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={go(1)}
            data-testid={`v2-gallery-next-${i}`}
            aria-label="Foto successiva"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 backdrop-blur p-2 text-[#1A1B18] hover:bg-[#1A1B18] hover:text-white transition-colors duration-300 md:opacity-0 md:group-hover/gallery:opacity-100"
          >
            <ChevronRight size={16} />
          </button>
          <div data-testid={`v2-gallery-dots-${i}`} className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.map((_, d) => (
              <span key={d} className={`h-1 rounded-full transition-all duration-300 ${d === idx ? "w-4 bg-[#1A1B18]" : "w-1.5 bg-[#1A1B18]/30"}`} />
            ))}
          </div>
        </>
      )}
      {zoom && (
        <div
          data-testid={`v2-gallery-zoom-${i}`}
          onClick={closeZoom}
          onWheel={onWheelZoom}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerUp}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200 overflow-hidden touch-none select-none"
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeZoom(); }}
            data-testid={`v2-gallery-zoom-close-${i}`}
            aria-label="Chiudi"
            className="absolute top-5 right-5 z-10 rounded-full bg-white/15 text-white p-2.5 hover:bg-white/30 transition-colors"
          >
            <X size={22} />
          </button>
          <img
            src={`${API}/files/${photos[idx]}`}
            alt={p.nome}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={onDblZoom}
            onMouseDown={onPointerDown}
            onTouchStart={onTouchStart}
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})` }}
            data-testid={`v2-gallery-zoom-img-${i}`}
            className={`max-h-[88vh] max-w-[92vw] object-contain shadow-2xl transition-transform duration-100 ${scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
          />
          <div className="absolute top-5 left-5 text-white/70 text-[11px] uppercase tracking-[0.15em] pointer-events-none">
            Doppio click o pizzica per ingrandire
          </div>
          {photos.length > 1 && (
            <>
              <button
                onClick={go(-1)}
                aria-label="Foto precedente"
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 rounded-full bg-white/15 text-white p-3 hover:bg-white/30 transition-colors"
              >
                <ChevronLeft size={26} />
              </button>
              <button
                onClick={go(1)}
                aria-label="Foto successiva"
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 rounded-full bg-white/15 text-white p-3 hover:bg-white/30 transition-colors"
              >
                <ChevronRight size={26} />
              </button>
              <div
                className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 max-w-[92vw] overflow-x-auto px-2 py-1"
                onClick={(e) => e.stopPropagation()}
              >
                {photos.map((ph, t) => (
                  <button
                    key={`${ph}-${t}`}
                    onClick={(e) => { e.stopPropagation(); setIdx(t); resetZoom(); }}
                    data-testid={`v2-gallery-thumb-${i}-${t}`}
                    aria-label={`Vai alla foto ${t + 1}`}
                    className={`shrink-0 w-14 h-14 rounded overflow-hidden border-2 transition-all ${t === idx ? "border-white scale-105" : "border-white/30 opacity-60 hover:opacity-100"}`}
                  >
                    <img src={`${API}/files/${ph}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const V2CollezioniCards = ({ onSelect }) => {
  const { t } = useLang();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/collections`).then((r) => setCollections(r.data)).catch(() => {});
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const mains = collections.filter((c) => !c.parent);
  if (!mains.length) return null;

  const cardImage = (c) => {
    if (c.image_path) return `${API}/files/${c.image_path}`;
    const first = products.find((p) => p.categoria === c.nome);
    if (first) return `${API}/files/${first.image_path}`;
    return "/images/sole-macro.webp";
  };

  return (
    <section data-testid="v2-collezioni" className="py-12 md:py-16 border-t border-[#1A1B18]/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#A86946] mb-8">{t("v2_coll_overline")}</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {mains.map((c, i) => {
            const sellable = c.online !== false && c.nome !== VISTA;
            return (
              <Reveal key={c.id} delay={i * 0.08}>
                <button
                  onClick={() => onSelect(c)}
                  data-testid={`v2-coll-card-${i}`}
                  className="group relative w-full h-52 md:h-60 overflow-hidden rounded-2xl text-left"
                >
                  <img
                    src={cardImage(c)}
                    alt={c.nome}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1B18]/75 via-[#1A1B18]/15 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between gap-3">
                    <div>
                      <p className="font-modern text-xl md:text-2xl font-medium tracking-tight text-white">{c.nome}</p>
                      {!sellable && (
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#E8B48E] mt-1">{t("v2_coll_instore")}</p>
                      )}
                    </div>
                    <span className="rounded-full border border-white/50 p-2 text-white group-hover:bg-[#A86946] group-hover:border-[#A86946] transition-colors duration-300 shrink-0">
                      <ArrowUpRight size={15} />
                    </span>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.1}>
          <div
            data-testid="v2-book-banner"
            className="mt-5 relative overflow-hidden rounded-2xl bg-[#1A1B18] text-white px-8 py-10 md:px-14 md:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8"
          >
            <div
              className="absolute inset-0 opacity-25 pointer-events-none"
              style={{ backgroundImage: "radial-gradient(120% 120% at 100% 0%, #A86946 0%, transparent 55%)" }}
              aria-hidden="true"
            />
            <div className="relative">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#E8B48E] mb-3">{t("book_banner_overline")}</p>
              <h3 className="font-modern text-3xl md:text-5xl font-medium tracking-tighter leading-[1.05] max-w-2xl">
                {t("book_banner_title")}
              </h3>
              <p className="mt-4 text-sm md:text-base text-white/70 max-w-xl leading-relaxed">{t("book_banner_sub")}</p>
            </div>
            <button
              onClick={scrollToPrenota}
              data-testid="v2-book-banner-cta"
              className="relative shrink-0 inline-flex items-center gap-3 rounded-full bg-[#A86946] text-white text-xs uppercase tracking-[0.18em] px-8 py-4 hover:bg-white hover:text-[#1A1B18] transition-colors duration-300"
            >
              {t("book_banner_cta")}
              <ArrowUpRight size={16} />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const V2Prodotti = ({ filter, setFilter, onAdd, tick = 0, sandbox = false }) => {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [tryonProduct, setTryonProduct] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const INITIAL_COUNT = 3;

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
    axios.get(`${API}/collections`).then((r) => setCollections(r.data)).catch(() => {});
  }, [tick]);

  const isSellable = (c) => c.online !== false && c.nome !== VISTA;
  const mains = collections.filter((c) => !c.parent && isSellable(c));
  const nonSellableMains = collections.filter((c) => !c.parent && !isSellable(c));
  const nonSellableSubs = collections.filter((c) => c.parent && nonSellableMains.some((m) => m.nome === c.parent));
  const nonSellableNames = nonSellableMains.map((c) => c.nome).concat(nonSellableSubs.map((c) => c.nome));
  const isNonSellableProduct = (p) => nonSellableNames.includes(p.categoria) || nonSellableNames.includes(p.sottocategoria);
  const active = filter === "tutte" ? (mains[0]?.nome || "tutte") : filter;
  const visible = (active === "tutte" ? products : products.filter((p) => p.categoria === active || p.sottocategoria === active)).filter((p) => !isNonSellableProduct(p));
  const ordered = [...visible.filter((p) => p.in_evidenza), ...visible.filter((p) => !p.in_evidenza)];
  const displayed = showAll ? ordered : ordered.slice(0, INITIAL_COUNT);
  const waText = (nome) =>
    encodeURIComponent(`Ciao Stylottica Ceglie! Mi interessa: ${nome}. È disponibile?`);

  return (
    <section id="v2-prodotti" data-testid="v2-prodotti" className="py-16 md:py-24 border-t border-[#1A1B18]/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#A86946] mb-4">{t("v2_prod_overline")}</p>
              <p className="text-base text-[#1A1B18]/60 max-w-lg">{t("v2_prod_sub")}</p>
            </div>
            <div data-testid="v2-filters" className="flex flex-wrap gap-x-6 gap-y-2">
              {mains.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => { setFilter(c.nome); setShowAll(false); }}
                  data-testid={`v2-filter-${i}`}
                  className={`text-[11px] uppercase tracking-[0.2em] pb-1 border-b transition-colors ${
                    active === c.nome ? "border-[#A86946] text-[#A86946]" : "border-transparent text-[#1A1B18]/50 hover:text-[#1A1B18]"
                  }`}
                >
                  {c.nome}
                </button>
              ))}
              {nonSellableMains.map((c) => (
                <button
                  key={c.id}
                  onClick={scrollToPrenota}
                  data-testid={`v2-filter-nosell-${c.nome === VISTA ? "vista" : c.id}`}
                  className="text-[11px] uppercase tracking-[0.2em] pb-1 border-b border-transparent text-[#A86946] font-semibold hover:border-[#A86946] transition-colors"
                >
                  {c.nome} · {t("cta_prenota")} →
                </button>
              ))}
            </div>
          </div>
        </Reveal>
        {visible.length === 0 ? (
          <p data-testid="v2-empty" className="text-sm text-[#1A1B18]/50 border-t border-[#1A1B18]/10 pt-12 text-center">
            {t("v2_empty")}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-10 gap-y-14">
            {displayed.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <article data-testid={`v2-product-${i}`} className="group">
                  <ProductGallery product={p} index={i} />
                  <div className="mt-5 flex items-baseline justify-between gap-3">
                    <h3 className="font-modern text-xl md:text-2xl font-medium tracking-tight">{p.nome}</h3>
                    <span className="text-sm font-semibold text-[#A86946] whitespace-nowrap">{eur(p.prezzo)}</span>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#1A1B18]/45 mt-1">
                    {p.categoria}{p.sottocategoria ? ` · ${p.sottocategoria}` : ""}
                  </p>
                  {p.disponibile === false && (
                    <span data-testid={`v2-unavailable-badge-${i}`} className="inline-block mt-2 rounded-full bg-[#1A1B18] text-white text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-1">
                      {t("vet_unavailable")}
                    </span>
                  )}
                  {(p.colore_lenti || p.taglia || p.misure) && (
                    <p data-testid={`v2-product-specs-${i}`} className="text-[11px] text-[#1A1B18]/55 mt-1.5">
                      {[p.colore_lenti && `${t("spec_lenti")} ${p.colore_lenti}`, p.taglia && `${t("spec_taglia")} ${p.taglia}`, p.misure].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <div className="mt-4 flex flex-col items-start gap-3">
                    {p.disponibile === false ? (
                      <button
                        disabled
                        data-testid={`v2-unavailable-${i}`}
                        className="inline-flex items-center gap-2 rounded-full bg-[#1A1B18]/10 text-[#1A1B18]/50 text-[10px] uppercase tracking-[0.18em] px-5 py-2.5 cursor-not-allowed"
                      >
                        {t("vet_unavailable")}
                      </button>
                    ) : (
                    <>
                    {p.azione === "visita" ? (
                      <button
                        onClick={scrollToPrenota}
                        data-testid={`v2-book-cta-${i}`}
                        className="inline-flex items-center gap-2 rounded-full bg-[#A86946] text-white text-[10px] uppercase tracking-[0.18em] px-5 py-2.5 hover:bg-[#1A1B18] transition-colors duration-300"
                      >
                        <ArrowUpRight size={13} />
                        {t("cta_prenota")}
                      </button>
                    ) : (
                      <button
                        onClick={() => onAdd(p)}
                        disabled={p.stock !== null && p.stock <= 0}
                        data-testid={`v2-add-cart-${i}`}
                        className="inline-flex items-center gap-2 rounded-full bg-[#1A1B18] text-white text-[10px] uppercase tracking-[0.18em] px-5 py-2.5 hover:bg-[#A86946] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ShoppingBag size={13} />
                        {p.stock !== null && p.stock <= 0 ? t("vet_out") : t("vet_add")}
                      </button>
                    )}
                    {p.tryon && (
                      <button
                        onClick={() => setTryonProduct(p)}
                        data-testid={`v2-tryon-${i}`}
                        className="inline-flex items-center gap-2 rounded-full border border-[#A86946] text-[#A86946] text-[10px] uppercase tracking-[0.18em] px-5 py-2.5 hover:bg-[#A86946] hover:text-white transition-colors duration-300"
                      >
                        <ScanFace size={13} />
                        {t("tryon_btn")}
                      </button>
                    )}
                    {p.azione !== "visita" && (
                      <a
                        href={`https://wa.me/393925218114?text=${waText(p.nome)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`v2-product-cta-${i}`}
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#1A1B18]/70 border-b border-[#1A1B18]/20 pb-0.5 hover:text-[#A86946] hover:border-[#A86946] transition-colors"
                      >
                        {t("v2_prod_cta")} <ArrowUpRight size={13} />
                      </a>
                    )}
                    </>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
        {visible.length > INITIAL_COUNT && (
          <Reveal>
            <div className="mt-14 text-center">
              <button
                onClick={() => setShowAll((s) => !s)}
                data-testid="v2-show-all"
                className="inline-flex items-center gap-2 rounded-full border border-[#1A1B18] text-[#1A1B18] text-[11px] uppercase tracking-[0.2em] px-7 py-3.5 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300"
              >
                {showAll ? t("v2_show_less") : `${t("v2_show_all")} (${visible.length})`}
              </button>
            </div>
          </Reveal>
        )}
      </div>
      {tryonProduct && (
        <TryOnModal
          product={tryonProduct}
          onClose={() => setTryonProduct(null)}
          experimental={sandbox}
          products={products}
          onAdd={onAdd}
        />
      )}
    </section>
  );
};

const V2Promo = () => {
  const { t, lang } = useLang();
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/promos`).then((r) => setPromos(r.data)).catch(() => {});
  }, []);

  if (!promos.length) return null;
  const p = promos[0];
  const locale = lang === "it" ? "it-IT" : lang === "de" ? "de-DE" : lang === "fr" ? "fr-FR" : "en-GB";
  const fmtD = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });

  return (
    <section data-testid="v2-promo-section" className="bg-[#A86946] text-white py-14 md:py-20 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center gap-8 md:gap-16">
        <Reveal>
          <div data-testid="v2-promo-value" className="font-modern text-5xl md:text-8xl leading-none font-medium tracking-tighter">
            {p.tipo === "finale" && p.prezzo_originale ? (
              <span className="flex flex-wrap items-baseline gap-4">
                <span className="line-through text-white/50 text-3xl md:text-5xl">{eur(p.prezzo_originale)}</span>
                <span>{eur(p.valore)}</span>
              </span>
            ) : p.tipo === "fisso" ? (
              `-${eur(p.valore)}`
            ) : p.tipo === "testo" ? (
              p.testo_offerta
            ) : (
              `-${p.percentuale}%`
            )}
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="text-xs uppercase tracking-[0.25em] text-white/70 mb-3 flex items-center gap-2">
            <Tag size={14} /> {t("promo_label")}
          </p>
          <h2 className="font-modern text-2xl md:text-4xl font-medium tracking-tighter max-w-xl">{p.titolo}</h2>
          {p.descrizione && <p className="mt-3 text-white/80 max-w-md text-sm md:text-base">{p.descrizione}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-5">
            <a
              href="#v2-prodotti"
              data-testid="v2-promo-cta"
              className="rounded-full bg-white text-[#A86946] text-xs uppercase tracking-[0.15em] px-7 py-3.5 hover:bg-[#1A1B18] hover:text-white transition-colors duration-300"
            >
              {t("promo_cta")}
            </a>
            {(p.valida_da || p.valida_fino) && (
              <p data-testid="v2-promo-dates" className="text-xs uppercase tracking-[0.2em] text-white/60">
                {p.valida_da && `${t("promo_da")} ${fmtD(p.valida_da)}`}
                {p.valida_da && p.valida_fino && " · "}
                {p.valida_fino && `${t("promo_valid")} ${fmtD(p.valida_fino)}`}
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const V2BrandStrip = () => {
  const { t } = useLang();
  const items = ["Police", t("mq_sole"), t("mq_vista"), t("mq_contatto"), t("mq_teen"), t("mq_nuove")];
  return (
    <div data-testid="v2-brand-strip" className="border-y border-[#1A1B18]/10 py-5 overflow-hidden">
      <Marquee speed={22} gradient={false}>
        {items.map((b) => (
          <span
            key={b}
            className="font-modern text-xl md:text-2xl font-medium text-[#1A1B18]/25 mx-8 md:mx-12 whitespace-nowrap"
          >
            {b} <span className="text-[#A86946]/50 text-base align-middle">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
};

const V2Contatti = () => {
  const { t } = useLang();
  const hours = [
    [t("day_mon"), "17:30 – 20:00 · " + t("mon_note")],
    [t("day_tuesat"), "09:00 – 12:45 · 17:30 – 20:00"],
    [t("day_sun"), t("closed")],
  ];
  return (
    <section id="v2-contatti" data-testid="v2-contatti" className="py-16 md:py-24 border-t border-[#1A1B18]/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#A86946] mb-12">{t("con_overline")}</p>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal>
            <h2 className="font-modern text-4xl md:text-6xl font-medium tracking-tighter leading-[1.05] mb-12">
              {t("con_title")}
            </h2>
            <div className="space-y-0">
              <a href="https://www.google.com/maps?q=Via+Cristoforo+Colombo+40,+Ceglie+Messapica+BR" target="_blank" rel="noopener noreferrer" data-testid="v2-address" className="flex items-center gap-4 py-5 border-t border-[#1A1B18]/10 hover:text-[#A86946] transition-colors">
                <MapPin size={18} className="text-[#A86946] shrink-0" />
                <span className="text-sm md:text-base">Via Cristoforo Colombo 40, 72013 Ceglie Messapica (BR)</span>
              </a>
              <a href="tel:+3908311981780" data-testid="v2-phone" className="flex items-center gap-4 py-5 border-t border-[#1A1B18]/10 hover:text-[#A86946] transition-colors">
                <Phone size={18} className="text-[#A86946] shrink-0" />
                <span className="text-sm md:text-base">0831 19 81 780 · 392 521 8114 {t("con_phone_note")}</span>
              </a>
              <a href="mailto:infostylottica@libero.it" data-testid="v2-email" className="flex items-center gap-4 py-5 border-t border-b border-[#1A1B18]/10 hover:text-[#A86946] transition-colors">
                <Mail size={18} className="text-[#A86946] shrink-0" />
                <span className="text-sm md:text-base">infostylottica@libero.it</span>
              </a>
            </div>
            <div className="mt-10 space-y-3">
              {hours.map(([d, h]) => (
                <div key={d} className="flex justify-between text-sm">
                  <span className="font-modern text-lg font-medium">{d}</span>
                  <span className="text-[#1A1B18]/60 text-right">{h}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-10">
              <a href="https://www.instagram.com/stylottica" target="_blank" rel="noopener noreferrer" data-testid="v2-instagram" aria-label="Instagram" className="border border-[#1A1B18]/15 p-3 hover:bg-[#1A1B18] hover:text-white transition-colors">
                <Instagram size={18} />
              </a>
              <a href="https://www.facebook.com/stylottica" target="_blank" rel="noopener noreferrer" data-testid="v2-facebook" aria-label="Facebook" className="border border-[#1A1B18]/15 p-3 hover:bg-[#1A1B18] hover:text-white transition-colors">
                <Facebook size={18} />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full min-h-[400px] border border-[#1A1B18]/10">
              <iframe
                title="Mappa Stylottica Ceglie"
                data-testid="v2-map"
                src="https://www.google.com/maps?q=Via+Cristoforo+Colombo+40,+72013+Ceglie+Messapica+BR&output=embed"
                className="w-full h-full min-h-[400px] border-0"
                loading="lazy"
              />
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Via+Cristoforo+Colombo+40,+72013+Ceglie+Messapica+BR"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="v2-directions"
              className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#A86946] border-b border-[#A86946]/40 pb-0.5 hover:text-[#1A1B18] hover:border-[#1A1B18] transition-colors"
            >
              <Navigation size={13} /> {t("con_directions")} →
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const V2Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="v2-footer" className="bg-[#1A1B18] text-white py-16">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between gap-8">
        <div>
          <img
            src="/images/logo-stylottica-mark.png?v=3"
            alt="Stylottica Ceglie"
            className="brand-logo h-14 w-auto mb-4"
          />
          <p className="font-modern text-2xl font-medium">Stylottica Ceglie</p>
          <p className="mt-3 text-sm text-white/50 max-w-xs">{t("footer_tag")}</p>
          <p data-testid="v2-footer-legal" className="mt-5 text-xs text-white/40 leading-relaxed max-w-xs">
            Stylottica &amp; C. s.a.s. · P.IVA 02325720742<br />
            Via Cristoforo Colombo 40, 72013 Ceglie Messapica (BR)
          </p>
        </div>
        <div className="text-sm text-white/50 space-y-2 md:text-right">
          <p>{t("footer_hours1")}</p>
          <p>{t("footer_hours2")}</p>
          <p className="pt-4">
            <Link to="/faq" data-testid="v2-footer-faq-link" className="hover:text-white transition-colors">FAQ</Link>
            {" · "}
            <a href="/admin" data-testid="v2-admin-link" className="hover:text-white transition-colors">{t("footer_admin")}</a>
            {" · "}
            <a href="/privacy" data-testid="v2-privacy-link" className="hover:text-white transition-colors">Privacy</a>
            {" · "}
            <a href="/cookie" data-testid="v2-cookie-link" className="hover:text-white transition-colors">Cookie</a>
            {" · "}
            <a href="/condizioni-di-vendita" data-testid="v2-condizioni-link" className="hover:text-white transition-colors">Condizioni di Vendita</a>
          </p>
          <p className="pt-2 text-xs text-white/30">© {new Date().getFullYear()} Stylottica &amp; C. s.a.s. · {t("footer_rights")}</p>
        </div>
      </div>
    </footer>
  );
};

export default function V2Home({ sandbox = false }) {
  const [filter, setFilter] = useState("tutte");
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [siteCfg, setSiteCfg] = useState({});
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [bookTipo, setBookTipo] = useState(null);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const r = await axios.get(`${API}/site-config`);
        if (cancelled) return;
        setSiteCfg(r.data || {});
        setCfgLoaded(true);
      } catch (e) {
        if (cancelled) return;
        if (attempt < 3) {
          setTimeout(() => load(attempt + 1), 800 * (attempt + 1));
        } else {
          setCfgLoaded(true);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, "");
    const wantsBooking = path === "/prenota" || window.location.hash === "#prenota";
    if (!wantsBooking) return;
    const tipo = new URLSearchParams(window.location.search).get("tipo");
    if (tipo === "vista" || tipo === "lenti") setBookTipo(tipo);
    const t = setTimeout(() => {
      document.querySelector("#prenota")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 700);
    return () => clearTimeout(t);
  }, []);

  const chiPhoto = siteCfg.chi_siamo_photo
    ? resolveImg(siteCfg.chi_siamo_photo)
    : cfgLoaded
    ? "/images/chi-siamo.webp"
    : null;
  const heroSlides = siteCfg.hero_slides?.length
    ? siteCfg.hero_slides.map((p, i) => ({ src: resolveImg(p), alt: `Stylottica Ceglie — foto ${i + 1}` }))
    : HERO_SLIDES;

  const selectCollection = (c) => {
    const sellable = c.online !== false && c.nome !== VISTA;
    if (sellable) {
      setFilter(c.nome);
      document.querySelector("#v2-prodotti")?.scrollIntoView({ behavior: "smooth" });
    } else {
      scrollToPrenota();
    }
  };

  const addToCart = (p) => {
    setCart((prev) => {
      const ex = prev.find((i) => i.id === p.id);
      if (ex) {
        if (p.stock != null && ex.qty >= p.stock) return prev;
        return prev.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...prev, { id: p.id, nome: p.nome, prezzo: p.prezzo, image_path: p.image_path, stock: p.stock ?? null, qty: 1 }];
    });
    setCartOpen(true);
  };

  return (
    <div className="v2-modern bg-[#FAF9F6] text-[#1A1B18]">
      <V2Header cartCount={cartCount} onCart={() => setCartOpen(true)} />
      <V2Hero slides={heroSlides} />
      <V2Storia photoUrl={chiPhoto} />
      <Services compact />
      <V2CollezioniCards onSelect={selectCollection} />
      <V2Prodotti filter={filter} setFilter={setFilter} onAdd={addToCart} tick={tick} sandbox={sandbox} />
      <V2Promo />
      <V2BrandStrip />
      <Reviews compact />
      <Prenota compact initialAmbito={bookTipo} />
      <V2Contatti />
      <V2Footer />
      <CartDrawer
        cart={cart}
        setCart={setCart}
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        onOrdered={() => setTick((x) => x + 1)}
      />
    </div>
  );
}
